import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge } from '../../components/Badge';
import { Button } from '../../components/Button';
import { EmptyState } from '../../components/EmptyState';
import { ErrorNotice } from '../../components/ErrorNotice';
import { SkeletonTable } from '../../components/Skeleton';
import { useRetentionJobs, useRunRetention } from './queries';

/**
 * Die Aufbewahrungslaeufe -- F5-13, K-152.
 *
 * **Der Probelauf ist die Vorgabe, und der scharfe Lauf braucht eine
 * Rueckfrage.** Er loescht -- und anders als beim Loeschen eines einzelnen
 * Datensatzes weiss vorher niemand, wie viele es sind. Genau dafuer ist der
 * Probelauf da: Er zaehlt und aendert nichts. Wer eine Frist scharf schaltet,
 * ohne sie vorher gezaehlt zu haben, erfaehrt von einem Zahlendreher in der
 * Aufbewahrungsfrist, wenn die Daten fort sind.
 *
 * Die Liste darunter ist der Nachweis: was wann in welchem Umfang entfernt
 * wurde, samt der Fristen, die **damals** galten.
 */
export function RetentionPanel() {
  const { t } = useTranslation();
  const jobs = useRetentionJobs();
  const run = useRunRetention();

  const [confirming, setConfirming] = useState(false);

  const preview = run.data && run.variables ? run.data : null;

  return (
    <div className="stack">
      <section className="card">
        <h2>{t('privacy.retentionRun')}</h2>
        <p className="table__muted">{t('privacy.retentionHint')}</p>

        <div className="row">
          <Button
            variant="primary"
            busy={run.isPending && run.variables}
            onClick={() => {
              setConfirming(false);
              run.mutate(true);
            }}
          >
            {t('privacy.dryRun')}
          </Button>
          <Button
            variant="secondary"
            onClick={() => {
              setConfirming(true);
            }}
          >
            {t('privacy.liveRun')}
          </Button>
        </div>

        {run.isError ? <ErrorNotice error={run.error} /> : null}

        {preview ? (
          <div className="notice notice--info" role="status">
            <p>{t('privacy.dryRunResult', { count: preview.affected_rows })}</p>
            <ul>
              {Object.entries(preview.outcome).map(([key, value]) => (
                <li key={key}>
                  {t(`retentionScope.${key}`)}: {value}
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </section>

      {confirming ? (
        <div className="notice notice--warning" role="alertdialog" aria-live="assertive">
          <p>{t('privacy.liveRunConfirm')}</p>
          <div className="row">
            <Button
              variant="primary"
              busy={run.isPending && !run.variables}
              onClick={() => {
                run.mutate(false, {
                  onSuccess: () => {
                    setConfirming(false);
                  },
                });
              }}
            >
              {t('privacy.liveRunConfirmAction')}
            </Button>
            <Button
              variant="ghost"
              onClick={() => {
                setConfirming(false);
              }}
            >
              {t('app.cancel')}
            </Button>
          </div>
        </div>
      ) : null}

      <section className="card card--flush">
        {jobs.isPending ? <SkeletonTable columns={4} /> : null}
        {jobs.isError ? <ErrorNotice error={jobs.error} /> : null}

        {jobs.data?.length === 0 ? (
          <EmptyState icon="privacy" title={t('privacy.noJobs')} hint={t('privacy.noJobsHint')} />
        ) : null}

        {jobs.data && jobs.data.length > 0 ? (
          <div className="table-wrapper">
            <table className="table">
              <thead>
                <tr>
                  <th scope="col">{t('privacy.startedAt')}</th>
                  <th scope="col">{t('privacy.mode')}</th>
                  <th scope="col">{t('privacy.affectedRows')}</th>
                  <th scope="col">{t('privacy.outcome')}</th>
                </tr>
              </thead>
              <tbody>
                {jobs.data.map((job) => (
                  <tr key={job.id}>
                    <td className="table__muted">{new Date(job.started_at).toLocaleString()}</td>
                    <td>
                      {job.dry_run ? (
                        <Badge tone="info">{t('privacy.dryRun')}</Badge>
                      ) : (
                        <Badge tone="warning">{t('privacy.liveRun')}</Badge>
                      )}
                    </td>
                    <td className="table__number">{job.affected_rows}</td>
                    <td className="table__muted">
                      {Object.entries(job.outcome)
                        .map(([key, value]) => `${t(`retentionScope.${key}`)}: ${String(value)}`)
                        .join(' · ')}
                      {job.error ? ` — ${job.error}` : ''}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </section>
    </div>
  );
}
